/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-18
 */

// Add third-party dependencies.
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Stack,
  Typography,
  Chip,
  IconButton,
  Box,
  Autocomplete,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

// Add local dependencies.
import {
  useCreateChecklistMessageMutation,
  useUpdateChecklistMessageMutation,
  useSendChecklistMessageMutation,
  useGetMasterItemsQuery,
} from '../../../api/classEssentialsApi';
import type {
  ChecklistMessage,
  MasterItem,
} from '../../../types/classEssentials';

/**
 * Props for ChecklistMessageDialog component
 */
interface ChecklistMessageDialogProps {
  open: boolean;
  onClose: () => void;
  studioId: string;
  message: ChecklistMessage | null;
}

/**
 * Recipient form data
 */
interface RecipientFormData {
  email: string;
  parent_name: string;
  student_name: string;
}

const EMPTY_RECIPIENT: RecipientFormData = {
  email: '',
  parent_name: '',
  student_name: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Trim a recipient's fields.
 *
 * @param recipient - Recipient as typed in the form
 * @returns The recipient with surrounding whitespace removed
 */
const normalizeRecipient = (
  recipient: RecipientFormData
): RecipientFormData => ({
  email: recipient.email.trim(),
  parent_name: recipient.parent_name.trim(),
  student_name: recipient.student_name.trim(),
});

/**
 * Validate a recipient against the recipients already in the list.
 *
 * @param candidate - Normalized recipient to validate
 * @param existing - Recipients already added
 * @returns An error message, or null when the recipient is valid
 */
const validateRecipient = (
  candidate: RecipientFormData,
  existing: RecipientFormData[]
): string | null => {
  if (!candidate.email) {
    return 'Email address is required';
  }
  if (!EMAIL_REGEX.test(candidate.email)) {
    return 'Please enter a valid email address';
  }
  const email = candidate.email.toLowerCase();
  if (existing.some(r => r.email.toLowerCase() === email)) {
    return 'This email address has already been added';
  }
  return null;
};

/**
 * Checklist Message Dialog component.
 * Form dialog for creating and editing checklist messages.
 *
 * @param props - Component props
 * @returns {React.ReactElement} The checklist message dialog component
 */
const ChecklistMessageDialog: React.FC<ChecklistMessageDialogProps> = ({
  open,
  onClose,
  studioId,
  message,
}) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedItems, setSelectedItems] = useState<MasterItem[]>([]);
  const [recipients, setRecipients] = useState<RecipientFormData[]>([]);
  const [currentRecipient, setCurrentRecipient] =
    useState<RecipientFormData>(EMPTY_RECIPIENT);
  const [error, setError] = useState<string | null>(null);

  const { data: masterItems = [], isLoading: loadingItems } =
    useGetMasterItemsQuery(studioId);
  const [createMessage, { isLoading: isCreating }] =
    useCreateChecklistMessageMutation();
  const [updateMessage, { isLoading: isUpdating }] =
    useUpdateChecklistMessageMutation();
  const [sendMessage, { isLoading: isSending }] =
    useSendChecklistMessageMutation();

  const isLoading = isCreating || isUpdating || isSending;

  // Reset form when dialog opens/closes or message changes
  useEffect(() => {
    if (open) {
      if (message) {
        setTitle(message.title || '');
        setNotes(message.notes || '');

        // Load recipients from message
        if (message.recipients && message.recipients.length > 0) {
          setRecipients(
            message.recipients.map(r => ({
              email: r.email,
              parent_name: r.parent_name || '',
              student_name: r.student_name || '',
            }))
          );
        } else {
          setRecipients([]);
        }

        // Load selected items from message
        if (message.items && message.items.length > 0) {
          // Match items from message with master items
          const itemIds = message.items.map(item => item.master_item);
          const matchedItems = masterItems.filter(item =>
            itemIds.includes(item.id)
          );
          setSelectedItems(matchedItems);
        } else {
          setSelectedItems([]);
        }
      } else {
        setTitle('');
        setNotes('');
        setSelectedItems([]);
        setRecipients([]);
      }
      setCurrentRecipient(EMPTY_RECIPIENT);
      setError(null);
    }
  }, [open, message, masterItems]);

  const handleAddRecipient = () => {
    const candidate = normalizeRecipient(currentRecipient);
    const validationError = validateRecipient(candidate, recipients);
    if (validationError) {
      setError(validationError);
      return;
    }

    setRecipients([...recipients, candidate]);
    setCurrentRecipient(EMPTY_RECIPIENT);
    setError(null);
  };

  const handleRemoveRecipient = (index: number) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent, sendNow: boolean = false) => {
    e.preventDefault();
    setError(null);

    // A filled-in recipient row counts even if "Add Recipient" wasn't
    // clicked. Validate it rather than dropping it silently.
    let finalRecipients = recipients;
    const pending = normalizeRecipient(currentRecipient);
    if (pending.email || pending.parent_name || pending.student_name) {
      const validationError = validateRecipient(pending, recipients);
      if (validationError) {
        setError(validationError);
        return;
      }
      finalRecipients = [...recipients, pending];
    }

    // Validate recipients
    if (finalRecipients.length === 0) {
      setError('At least one recipient is required');
      return;
    }

    // Validate items
    if (selectedItems.length === 0) {
      setError('At least one item must be selected');
      return;
    }

    try {
      let messageId: string;

      if (message) {
        // Update - don't include studio field
        const updateData = {
          title: title.trim(),
          notes: notes.trim(),
          recipients: finalRecipients.map(r => ({
            email: r.email,
            parent_name: r.parent_name,
            student_name: r.student_name,
          })),
          items: selectedItems.map((item, index) => ({
            master_item: item.id,
            display_order: index,
          })),
        };
        await updateMessage({ id: message.id, data: updateData }).unwrap();
        messageId = message.id;
      } else {
        // Create - include studio field
        const createData = {
          studio: studioId,
          title: title.trim(),
          notes: notes.trim(),
          recipients: finalRecipients.map(r => ({
            email: r.email,
            parent_name: r.parent_name,
            student_name: r.student_name,
          })),
          items: selectedItems.map((item, index) => ({
            master_item: item.id,
            display_order: index,
          })),
        };
        const result = await createMessage(createData).unwrap();
        messageId = result.id;
      }

      // If sendNow is true, send the message immediately
      if (sendNow) {
        await sendMessage(messageId).unwrap();
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to save message:', err);
      setError(
        err?.data?.detail || 'Failed to save message. Please try again.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={e => handleSubmit(e, false)}>
        <DialogTitle>
          {message ? 'Edit Checklist Message' : 'Create Checklist Message'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={3} sx={{ mt: 0.5 }}>
            {/* Message Details */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Message Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Title (Optional)"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., Ballet 101 Requirements"
                  disabled={isLoading}
                />

                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Optional instructions for parents..."
                  disabled={isLoading}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Item Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Items *
              </Typography>
              <Autocomplete
                multiple
                options={masterItems.filter(item => item.is_available)}
                value={selectedItems}
                onChange={(_, newValue) => setSelectedItems(newValue)}
                getOptionLabel={option => option.name}
                groupBy={option => option.category || 'Uncategorized'}
                renderInput={params => (
                  <TextField
                    {...params} // eslint-disable-line react/jsx-props-no-spreading
                    placeholder="Select items to include in checklist..."
                    disabled={isLoading || loadingItems}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })} // eslint-disable-line react/jsx-props-no-spreading
                      disabled={isLoading}
                    />
                  ))
                }
              />
            </Box>

            <Divider />

            {/* Recipients */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Recipients *
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                Enter a parent&apos;s email below. To send to more than one
                parent, click &quot;Add Recipient&quot; after each one.
              </Typography>

              {/* Current Recipients List */}
              {recipients.length > 0 && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {recipients.map((recipient, index) => (
                    <Box
                      key={index} // eslint-disable-line react/no-array-index-key
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {recipient.email}
                        </Typography>
                        {(recipient.parent_name || recipient.student_name) && (
                          <Typography variant="caption" color="text.secondary">
                            {[recipient.parent_name, recipient.student_name]
                              .filter(Boolean)
                              .join(' - ')}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRecipient(index)}
                        disabled={isLoading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Add Recipient Form */}
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Parent Email *"
                  type="email"
                  value={currentRecipient.email}
                  onChange={e =>
                    setCurrentRecipient({
                      ...currentRecipient,
                      email: e.target.value,
                    })
                  }
                  placeholder="parent@example.com"
                  disabled={isLoading}
                />

                <TextField
                  fullWidth
                  label="Parent Name (Optional)"
                  value={currentRecipient.parent_name}
                  onChange={e =>
                    setCurrentRecipient({
                      ...currentRecipient,
                      parent_name: e.target.value,
                    })
                  }
                  placeholder="e.g., Jane Smith"
                  disabled={isLoading}
                />

                <TextField
                  fullWidth
                  label="Student Name (Optional)"
                  value={currentRecipient.student_name}
                  onChange={e =>
                    setCurrentRecipient({
                      ...currentRecipient,
                      student_name: e.target.value,
                    })
                  }
                  placeholder="e.g., Emma Smith"
                  disabled={isLoading}
                />

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddRecipient}
                  disabled={isLoading || !currentRecipient.email.trim()}
                  variant="outlined"
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Recipient
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {message ? 'Update Draft' : 'Save as Draft'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={e => handleSubmit(e, true)}
            disabled={
              isLoading ||
              (recipients.length === 0 && !currentRecipient.email.trim())
            }
          >
            Save & Send
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChecklistMessageDialog;
